import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { Observable } from '../utils/Observable';
import { isObject, isString } from '../utils/typeGuards';
import { allowedOrigins } from '../constants';

interface MessageData {
  type: string;
  message: object;
}

const isMessageData = (data: any): data is MessageData => {
  return (
    isObject(data) &&
    isString(data.type) &&
    isObject(data.message) &&
    data?.type?.includes(':')
  );
};

const embeddedAcademyKey = 'embeddedAcademy' as const;

const formatMessage = (
  messengerKey: string,
  messageKey: string,
  message: any
) => {
  const messageToSend = {
    type: `${embeddedAcademyKey}:${messengerKey}:${messageKey}`,
    message,
  };
  return messageToSend;
};

const parseMessage = (data: any, key: string) => {
  if (
    isMessageData(data) &&
    data?.type?.includes(`${embeddedAcademyKey}:${key}`)
  ) {
    const parts = data.type.split(':');
    const event = parts[parts.length - 1];
    if (event) {
      return [event, data.message] as const;
    }
  }
};

type MessageHandler<S> = (
  message: any,
  setState: (newState: S) => void
) => void;
type MessageHandlerRecord<S> = Record<string, MessageHandler<S>>;

const handshakeMessageKey = 'handshake' as const;
const readyMessageKey = 'ready' as const;

const dispatchHandlers = <S>(
  messageData: ReturnType<typeof parseMessage>,
  set: (newState: S) => void,
  messageHandlers?: MessageHandlerRecord<S>
) => {
  if (messageHandlers && messageData) {
    const [handlerKey, message] = messageData;
    const handler = messageHandlers[handlerKey];
    if (handler) {
      handler(message, set);
    }
  }
};

class OutsideFrameMessenger<S> extends Observable<S> {
  iFrame: HTMLIFrameElement | null = null;
  key: string;
  messageHandlers?: MessageHandlerRecord<S>;
  ownMessageHandlers?: MessageHandlerRecord<S> = {
    [readyMessageKey]: () => {
      this.sendMessage(handshakeMessageKey, {
        domain: window.location.origin,
      });
    },
  };

  constructor(
    iFrame: HTMLIFrameElement,
    key: string,
    messageHandlers: Record<string, MessageHandler<S>> = {}
  ) {
    super();
    this.key = key;
    this.iFrame = iFrame;
    this.messageHandlers = messageHandlers;

    this.setupMessageHandlers();
  }

  setupMessageHandlers = () => {
    const handleMessage = (e: MessageEvent) => {
      if (e.source !== this.iFrame?.contentWindow) return;
      if (!allowedOrigins.includes(e.origin)) return;
      const msgData = parseMessage(e.data, this.key);
      if (msgData) {
        dispatchHandlers(msgData, this.setState, this.ownMessageHandlers);
        dispatchHandlers(msgData, this.setState, this.messageHandlers);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  };

  sendMessage = (messageKey: string, message: any) => {
    const messageToSend = formatMessage(this.key, messageKey, message);
    if (this.iFrame && this.iFrame.contentWindow) {
      this.iFrame.contentWindow.postMessage(
        messageToSend,
        new URL(this.iFrame.src).origin
      );
    }
  };
}

class InsideFrameMessenger<S> extends Observable<S> {
  parent: Window = window.parent;
  key: string;
  outsideFrameDomain?: string;
  messageHandlers?: MessageHandlerRecord<S>;
  ownMessageHandlers?: MessageHandlerRecord<S> = {
    [handshakeMessageKey]: (message) => {
      if (!allowedOrigins.includes(message.domain)) {
        console.error('Invalid handshake message domain', message.domain);
        return;
      }
      this.outsideFrameDomain = message.domain;
    },
  };

  constructor(key: string) {
    super();
    this.key = key;
  }

  setupMessageHandlers = (
    messageHandlers: MessageHandlerRecord<S> = {},
    allowExternal?: boolean
  ) => {
    this.messageHandlers = messageHandlers;

    const handleMessage = (e: MessageEvent) => {
      if (e.source !== window.parent) return;
      if (!allowExternal && !allowedOrigins.includes(e.origin)) return;
      const msgData = parseMessage(e.data, this.key);
      dispatchHandlers(msgData, this.setState, this.ownMessageHandlers);
      dispatchHandlers(msgData, this.setState, this.messageHandlers);
    };

    window.addEventListener('message', handleMessage);
    this.sendMessage(readyMessageKey, { ready: true });

    return () => window.removeEventListener('message', handleMessage);
  };

  sendMessage = (messageKey: string, message: any, allowExternal?: boolean) => {
    const messageToSend = formatMessage(this.key, messageKey, message);

    if (messageKey === readyMessageKey) {
      allowedOrigins.forEach((origin) => {
        window.parent.postMessage(messageToSend, origin);
      });
    } else if (this.outsideFrameDomain) {
      window.parent.postMessage(messageToSend, this.outsideFrameDomain);
    } else if (allowExternal) {
      window.parent.postMessage(messageToSend, '*');
    }
  };
}

type MessengerType = 'syncHeight' | 'dynamic' | 'static';

/**
 * For each messenger type, we keep a registry of iframes and their corresponding messengers.
 * This allows us to have a single messenger for each iframe. Hooks that use the messenger store
 * will always be reading the same data from the same source.
 */
const outsideMessengerRegistry = {
  syncHeight: new Map<HTMLIFrameElement, OutsideFrameMessenger<any>>(),
  dynamic: new Map<HTMLIFrameElement, OutsideFrameMessenger<any>>(),
  static: new Map<HTMLIFrameElement, OutsideFrameMessenger<any>>(),
};
const insideMessengerRegistry: Record<
  MessengerType,
  InsideFrameMessenger<any>
> = {
  syncHeight: new InsideFrameMessenger<any>('syncHeight'),
  dynamic: new InsideFrameMessenger<any>('dynamic'),
  static: new InsideFrameMessenger<any>('static'),
};

const noop = () => {};
const getNullSyncStore = <S>() =>
  [
    () => {
      return noop;
    },
    noop as () => S,
  ] as const;

export const useOutsideFrameMessenger = <S>({
  key,
  messageHandlers,
}: {
  key: MessengerType;
  messageHandlers?: Record<string, MessageHandler<S>>;
}) => {
  const [messenger, setMessenger] = useState<OutsideFrameMessenger<S>>();
  const [isReady, setIsReady] = useState(false);

  const registry = outsideMessengerRegistry[key];

  const [subscribe, getState] = messenger
    ? [messenger.subscribe, messenger.getState]
    : getNullSyncStore<S>();

  const state = useSyncExternalStore(subscribe, getState);

  const registerIFrame = useCallback(
    (iFrame: HTMLIFrameElement | null) => {
      if (!iFrame) return;
      if (!registry.has(iFrame)) {
        const handlers = {
          ...messageHandlers,
          [readyMessageKey]: () => {
            setIsReady(true);
          },
        };
        const newMessenger = new OutsideFrameMessenger<S>(
          iFrame,
          key,
          handlers
        );
        registry.set(iFrame, newMessenger);
        setMessenger(newMessenger);
      } else {
        setMessenger(registry.get(iFrame));
      }
    },
    [key, messageHandlers, registry]
  );

  return {
    isReady,
    state,
    registerIFrame,
    sendMessage: messenger?.sendMessage,
  };
};

export const useInsideFrameMessenger = <S>({
  key,
  messageHandlers,
  allowExternal,
}: {
  key: MessengerType;
  messageHandlers?: Record<string, MessageHandler<S>>;
  allowExternal?: boolean;
}) => {
  const messenger = insideMessengerRegistry[key];

  useEffect(() => {
    const unsubscribe = messenger.setupMessageHandlers(
      messageHandlers,
      allowExternal
    );

    return () => {
      unsubscribe();
    };
  }, [allowExternal, key, messageHandlers, messenger]);

  const state = useSyncExternalStore<S | undefined>(
    messenger.subscribe,
    messenger.getState
  );

  return {
    state,
    sendMessage: messenger.sendMessage,
  };
};

export const simpleStateSetHandler = (
  message: any,
  setState: (newState: any) => void
) => {
  setState(message);
};
